/**
 * Tracks how many automatic recovery passes a given page failure has already been given.
 *
 * Lives in its own module for two reasons. Fast Refresh requires a component file to export only
 * components, and — more importantly — this state MUST outlive the error fallback's mount. Clearing
 * the boundary tears that component down and the next failure arrives as a BRAND NEW `Error`
 * instance, so a counter held in a `useRef`/`useState` (or keyed on error identity) resets itself on
 * every pass and bounds nothing at all. Module scope, keyed on the error's TEXT, is what survives.
 */

/**
 * How many automatic passes a single failure gets before the user is shown the error screen.
 *
 * The bound is the whole safety argument. Unbounded auto-recovery against a permanently-broken
 * backend is an invisible infinite loop: the user watches a spinner forever, with no way out and no
 * way to tell us anything is wrong. Two attempts catch the overwhelmingly common case — a request
 * abandoned by a lifecycle event, which succeeds the moment it is re-issued — and anything that
 * survives them has earned a visible, escapable screen.
 */
export const MAX_AUTOMATIC_RECOVERY_ATTEMPTS = 2;

const attemptsByErrorText = new Map<string, number>();

function errorTextKey(error: Error): string {
  return `${error.name}: ${error.message}`;
}

/** Whether this failure still has an automatic recovery pass left. */
export function hasAutomaticErrorRecoveryAttemptsLeft(error: Error): boolean {
  return (attemptsByErrorText.get(errorTextKey(error)) ?? 0) < MAX_AUTOMATIC_RECOVERY_ATTEMPTS;
}

/** Record that an automatic recovery pass was started for this failure. */
export function recordAutomaticErrorRecoveryAttempt(error: Error): void {
  const key = errorTextKey(error);
  attemptsByErrorText.set(key, (attemptsByErrorText.get(key) ?? 0) + 1);
}

/**
 * Forget the recorded attempts.
 *
 * A user who presses "Try again" is explicitly asking for a fresh start — someone who waited out a
 * network outage should get the silent recovery back — so the count is cleared there. Also used by
 * the tests to isolate cases that share an error message.
 */
export function resetAutomaticErrorRecoveryAttempts(): void {
  attemptsByErrorText.clear();
}
